// SSG-safe: JSON-LD emitted via SEO/Helmet at render time.
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import SEO from "@/components/site/SEO";
import { useReveal } from "@/hooks/useReveal";
import { CALENDLY_URL } from "@/lib/constants";
import { CheckCircle2 } from "lucide-react";


export type CityPageProps = {
  city: string;
  slug: string;
  intro: string;
  description: string;
};

export default function CityPageTemplate({ city, slug, intro, description }: CityPageProps) {
  useReveal();
  const canonical = `https://finable.de/finanzberatung-${slug}`;
  const title = `Finanzberatung ${city} – Unabhängig & Digital | FINABLE`;

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "FINABLE GmbH",
    description: `Unabhängige, digitale Finanzberatung für Menschen aus ${city}.`,
    url: canonical,
    areaServed: { "@type": "City", name: city },
    priceRange: "Kostenlose Erstberatung",
    address: { "@type": "PostalAddress", addressCountry: "DE", addressLocality: city },
  };

  const whatWeDo = [
    "Unabhängige Beratung ohne Bankbindung",
    "100% digital – kein Termin vor Ort nötig",
    "Kostenlose Erstberatung ohne Verpflichtung",
    "Spezialisiert auf Steueroptimierung, Altersvorsorge, ETF und staatliche Förderungen",
  ];
  const services = [
    "Altersvorsorge: Rürup, Riester, bAV, ETF-Sparplan",
    "Steueroptimierung: Steuern sparen als Angestellter",
    "Staatliche Förderungen: Welche Zuschüsse stehen Dir zu?",
    "Vermögensaufbau: ETF, Immobilien, Geldanlage",
  ];
  const digital = [
    "Kein Termin, keine Anfahrt, kein Stress",
    "Unabhängig von Bankprodukten",
    "Bundesweit verfügbar",
    "Transparent und fair",
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO title={title} description={description} canonical={canonical} jsonLd={localBusinessSchema} />
      <Header />
      <main>
        <section className="pt-32 md:pt-40 pb-16 bg-gradient-hero text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-radial pointer-events-none" />
          <div className="container relative max-w-3xl text-center">
            <span className="reveal inline-block text-accent font-semibold uppercase tracking-wider text-sm">
              Finanzberatung {city}
            </span>
            <h1 className="reveal mt-4 font-display text-4xl md:text-6xl font-extrabold text-balance leading-[1.05]">
              Finanzberatung {city} – <span className="text-accent">Unabhängig, Digital & Kostenlos</span>
            </h1>
            <p className="reveal mt-6 text-lg md:text-xl text-white/85">{intro}</p>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-background">
          <div className="container max-w-3xl space-y-12">
            <div className="reveal">
              <h2 className="font-display text-3xl font-bold text-primary mb-6">Was FINABLE für Dich macht</h2>
              <ul className="space-y-3">
                {whatWeDo.map((t) => (
                  <li key={t} className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-1" /><span>{t}</span></li>
                ))}
              </ul>
            </div>

            <div className="reveal">
              <h2 className="font-display text-3xl font-bold text-primary mb-6">Unsere Leistungen für Kunden aus {city}</h2>
              <ul className="space-y-3">
                {services.map((t) => (
                  <li key={t} className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-1" /><span>{t}</span></li>
                ))}
              </ul>
            </div>

            <div className="reveal">
              <h2 className="font-display text-3xl font-bold text-primary mb-6">Warum digital besser ist</h2>
              <ul className="space-y-3">
                {digital.map((t) => (
                  <li key={t} className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-1" /><span>{t}</span></li>
                ))}
              </ul>
            </div>

            <div className="reveal text-center pt-8 border-t">
              <h2 className="font-display text-3xl font-bold text-primary mb-6">Jetzt kostenloses Erstgespräch vereinbaren</h2>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl bg-accent text-white px-8 py-4 font-semibold hover:bg-accent/90 transition-colors"
              >
                Kostenloses Erstgespräch buchen
              </a>

            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
